import {
  HeaderLogo,
  ContactUsLink,
  SearchLink,
  HamburgerMenuLink,
  FooterDisclaimer,
} from 'organisms';
import { useState, useEffect, useContext, KeyboardEvent } from 'react';
import { useResetPassword } from '@sentara-health/sentara-api-hooks-core';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import {
  getMaxLength,
  AlphaNumericUnderscore,
  NavigateConst,
  getAnalyticsBaseConfig,
  ANALYTICS_MESSAGES,
  SCREEN_NAMES,
  getErrorTypeByCode,
  domainForSegment,
} from 'common';
import { GlobalContext } from 'context';
import ResetPassword from './ResetPassword';
import {
  Label,
  ErrorMessage,
  Button,
  CheckIconBig,
  Headings,
  InputField,
  GlobalError,
  Loader,
  Link,
  Paragraph,
} from '@sentara-health/optimahealth_web';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import { AdobeFormConfig } from 'common/AdobeFormConfig';

const ForgotPassword = () => {
  const { logging } = useContext(GlobalContext);
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<boolean>(false);
  const [retriveSuccess, setRetriveSuccess] = useState<boolean>(false);
  const { state, resetPassword } = useResetPassword();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const {
    formSubmissionSucceeded,
    formSubmissionFailed,
    pageView,
    contentListingItemClicked,
  } = useAdobeAnalytics();

  const trackContentListing = (
    contentId: string,
    contentTitle: string,
    itemPosition: number
  ) => {
    contentListingItemClicked({
      segment: `${domainForSegment}${pathname}`,
      contentListing: {
        eventType: 'Content Listing Item Clicked',
        listing: {
          content: [{ contentId, contentTitle, itemPosition }],
        },
      },
    });
  };

  /* to get params from URL */
  let search = globalThis.location.search;
  let newParams = new URLSearchParams(search);
  let param: string = newParams.get('token') ?? '';

  let params: string = newParams.get('token') ?? '';
  let decodedParam = decodeURI(params);
  // when %20 is decoded to Space and sent to the Reset password API its throwing 462 error so converted to Plus sign as per legacy
  params = decodedParam.replaceAll(' ', '+');

  const {
    handleSubmit,
    register,
    formState: { errors, isValid },
  } = useForm({ mode: 'onChange' }); /* useForm props extract */
  useEffect(() => {
    if (state?.error) {
      setErrorMessage(true);
    }
  }, [state?.error]);

  // Username must start with a letter; spaces and disallowed characters are blocked
  const handleUsernameKeyDown = (
    event: KeyboardEvent<HTMLInputElement>
  ) => {
    const { key } = event;
    const value = event.currentTarget.value;
    const navigationKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight'];
    if (navigationKeys.includes(key)) return;
    if (
      !AlphaNumericUnderscore.test(key) ||
      key === ' ' ||
      (value.length === 0 && !/[a-zA-Z]/.test(key))
    ) {
      event.preventDefault();
    }
  };

  /* oncancel click navigate to sign in */
  const cancelClick = () => {
    logging?.trace({
      additionalDetails: getAnalyticsBaseConfig(),
      message: ANALYTICS_MESSAGES.FORGOT_PASSWORD_CANCEL,
      screenName: SCREEN_NAMES.FORGOT_PASSWORD,
      reason: ANALYTICS_MESSAGES.USER_CLICKED_CANCEL,
    });
    formSubmissionFailed({
      segment: `${domainForSegment}${pathname}`,
      form: AdobeFormConfig['forgotPasswordCancel'],
    });
    navigate(NavigateConst.signIn);
  };

  const onSubmit = async (data: any) => {
    const requestData = {
      user: { userId: data.userNameLabelForgotPassword },
      channel: 'Web',
      lang: 'en',
    };
    trackContentListing(
      '503',
      ANALYTICS_MESSAGES.FORGOT_PASSWORD_SAVE_CLICK,
      3
    );
    const response = await resetPassword(requestData);
    if (response.error) {
      setErrorMessage(true);
      logging?.error({
        additionalDetails: getAnalyticsBaseConfig(),
        apiDetails: {
          exception: {
            errorType: getErrorTypeByCode(response?.errorCode?.toString()),
            message: response?.errorDetails ?? response?.error,
            stack: response?.error,
          },
          response: { httpStatus: response?.errorCode },
        },
        message: ANALYTICS_MESSAGES.FORGOT_PASSWORD_EXCEPTION,
        screenName: SCREEN_NAMES.FORGOT_PASSWORD,
      });

      formSubmissionFailed({
        segment: `${domainForSegment}${pathname}`,
        form: {
          ...AdobeFormConfig['forgotPasswordError'],
          formFailReason: response?.errorDetails ?? response?.error,
        },
      });
      trackContentListing(
        '505',
        ANALYTICS_MESSAGES.FORGOT_PASSWORD_SAVE_FAILURE,
        5
      );
    } else if (response?.data === '') {
      setRetriveSuccess(true);

      logging?.trace({
        additionalDetails: getAnalyticsBaseConfig(),
        message: ANALYTICS_MESSAGES.FORGOT_PASSWORD_SUCCESS,
        screenName: SCREEN_NAMES.FORGOT_PASSWORD,
      });

      formSubmissionSucceeded({
        segment: `${domainForSegment}${pathname}`,
        form: AdobeFormConfig['forgotPasswordSuccess'],
      });

      pageView({
        segment: `${domainForSegment}${pathname}`,
        page: {
          eventType: 'pageView',
          pageName: 'Forgot Password Success',
          pageType: 'Authentication',
          siteSection: 'forgot-password',
        },
      });
    }
  };

  // Extract the nested ternary into a variable for clarity and to avoid compile errors
  let mainContent;
  if (param) {
    mainContent = (
      <div className="d-flex justify-content-center">
        <div className="col-lg-5 col-md-7 col-12">
          <ResetPassword params={params} />
        </div>
      </div>
    );
  } else if (!retriveSuccess) {
    mainContent = (
      <div className="row d-flex justify-content-center">
        <div className="col-lg-5 col-md-7 col-12">
          <form onSubmit={handleSubmit(onSubmit)} method="post">
            {state?.loading && <Loader />}
            {errorMessage && (
              <GlobalError variant="mt-4" dataTestId="usernameServiceErr12">
                {t('resetPassword.usernameServiceErr2')}
              </GlobalError>
            )}
            <Headings
              level={1}
              text={t('resetPassword.passwordHelp')}
              variant="medium"
              dataTestId="userNameForgot"
              className="my-5"
            />
            <div className="mt-1 mb-4">
              <div className="form-group">
                <Label
                  dataTestId={'userNameLabelForgotPassword'}
                  htmlFor="userNameLabelForgotPassword​"
                  className="pb-1"
                >
                  {t('resetPassword.userNameValidationText')}
                </Label>
                <InputField
                  placeholder={t('activation.userNameSmallPlaceholder')}
                  maxLength={getMaxLength.userName}
                  data-testid="userInputForgotPassword"
                  className={
                    errors.userNameLabelForgotPassword ? 'error-border' : ''
                  }
                  type="text"
                  id="userNameLabelForgotPassword"
                  {...register('userNameLabelForgotPassword', {
                    required: true,
                    // Username must begin with a letter, not a number or special character
                    validate: (value) => /^[a-zA-Z]/.test(value),
                  })}
                  onKeyDown={handleUsernameKeyDown}
                  onFocus={() => {
                    setErrorMessage(false);
                  }}
                />
                {errors.userNameLabelForgotPassword && (
                  <div className="mt-1">
                    <ErrorMessage data-testid={'forgotPasswordErrorMessage'}>
                      {t('resetPassword.enterUserName')}
                    </ErrorMessage>
                  </div>
                )}
                <Paragraph
                  level={2}
                  text={
                    <>
                      {t('resetPassword.didYou')}{' '}
                      <Link
                        to={'/login/forgot-username'}
                        data-testid="forgotUsernameLink"
                        className="text-decoration-underline small-link"
                        aria-label="Forgot username"
                      >
                        {t('resetPassword.forgetUserNameText')}
                      </Link>
                    </>
                  }
                  variant="regular"
                  className="form-text mt-4"
                />
              </div>
            </div>
            <div className="row mt-4">
              <div className="col-md-auto col-12 mb-4">
                <Button
                  dataTestId="submitButton"
                  type="submit"
                  variant="primary_btn"
                  className={`btn-md-width ${!isValid ? 'disabled' : null}`}
                >
                  {t('common.submit')}
                </Button>
              </div>
              <div className="col-md-auto col-12 mb-4">
                <Button
                  dataTestId="cancelButton"
                  type="button"
                  onClick={cancelClick}
                  variant="secondary_btn"
                  className="btn-md-width"
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  } else {
    mainContent = (
      <>
        {/* success content */}
        <div className="row d-flex justify-content-center mt-4 pt-3">
          <div className="col-lg-7 col-md-7 col-12 text-center">
            <CheckIconBig />
            <Headings
              level={1}
              text={t('common.activationSuccess')}
              variant="semiBold"
              dataTestId="alertShowSuccessMessage"
              className="my-4 py-1"
            />
            <Paragraph
              text={
                <Trans
                  i18nKey="resetPassword.contactSupportText"
                  components={{
                    link1: (
                      <Link
                        to={`tel:+${t('resetPassword.contactSupportNo')}`}
                        className="text-decoration-underline text-nowrap"
                        data-testid="ContactSupportPhoneLink"
                      >
                        {t('resetPassword.contactSupportNo')}
                      </Link>
                    ),
                  }}
                />
              }
              variant="regular"
              data-testid={'signInMaintenanceBanner'}
              className="lineBrake"
            />
            <div className="pt-5">
              <Button
                onClick={() => {
                  navigate(NavigateConst.signIn);
                }}
                type="button"
                dataTestId="redirectLoginPage"
                variant="primary_btn"
              >
                {t('common.returnLogin')}
              </Button>
            </div>
          </div>
        </div>
        {/* success content */}
      </>
    );
  }

  return (
    <div className="d-flex flex-column h-100">
      {/* Header section */}
      <header className="border-bottom">
        {/* Container for the header */}
        <div className="container-fluid">
          {/* Navigation bar */}
          <nav className="header container-lg d-flex align-items-center justify-content-between">
            <HeaderLogo />
            <div className="d-flex flex-no-wrap align-items-center">
              {/* Contact us link, visible on extra-large and large screens */}
              <div className="d-none d-xl-block d-lg-block d-md-none">
                <ContactUsLink showIcon={false} />
              </div>
              {/* Hamburger menu link, visible on large, medium, and small screens */}
              <div className="d-lg-none d-md-block d-sm-block">
                <HamburgerMenuLink showLogout={false} />
              </div>
            </div>
          </nav>
        </div>
      </header>
      {/* Main content container */}
      <div className="container-fluid">
        <div id="main-content" className="container-lg px-main">
          {/* Reset password component */}
          {mainContent}
        </div>
      </div>
      {/* Footer section */}
      <footer className="container-lg mt-auto">
        <hr className="mt-3 mb-4 pt-2" />
        {/* Component to display the footer disclaimer */}
        <FooterDisclaimer />
      </footer>
    </div>
  );
};

export default ForgotPassword;
